import { Link } from '@inertiajs/react'
import { ArrowUpRight } from 'lucide-react'

/* A schematic thumbnail beside a module that owns a detail page.
 *
 * The Distribution page stopped carrying full mocks — they made it
 * enormously tall — but a capability described in prose alone with no visual
 * at all reads as thinner than it is. This is the middle setting: a few lines
 * of wireframe suggesting the screen's shape, and a link to the page where the
 * real mock lives.
 *
 * `shape` picks the arrangement rather than a picture: `form` reads as a
 * builder (fields beside a side panel), `chart` as analytics (a plot over
 * ranked rows). Two shapes is enough — this is a cue, not an illustration, and
 * a third would be invention rather than information.
 */
export default function ModuleThumb({
  href,
  label,
  shape,
}: {
  href: string
  label: string
  shape: 'form' | 'chart'
}) {
  return (
    <Link
      href={href}
      prefetch
      className="group mt-5 flex items-center gap-4 rounded-xl border border-white/[0.08] bg-white/[0.02] p-3 no-underline transition-all hover:border-[#ff6a1a]/35 hover:bg-[#ff6a1a]/[0.04]"
    >
      <span
        className="flex h-[52px] w-[76px] shrink-0 flex-col gap-1 rounded-md border border-dashed border-white/[0.16] p-1.5"
        aria-hidden="true"
      >
        {shape === 'form' ? (
          <>
            <span className="flex gap-1">
              <span className="h-1 w-4 rounded-full bg-white/20" />
              <span className="h-1 w-3 rounded-full bg-white/10" />
            </span>
            <span className="flex flex-1 gap-1">
              <span className="flex flex-[1.4] flex-col gap-1">
                <span className="h-2 rounded-sm bg-white/[0.07]" />
                <span className="h-2 rounded-sm bg-white/[0.07]" />
              </span>
              <span className="flex-1 rounded-sm border border-white/10 bg-white/[0.04]" />
            </span>
          </>
        ) : (
          <>
            <span className="relative flex-[1.3] overflow-hidden rounded-sm bg-white/[0.04]">
              <span className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#ff6a1a]/30 to-transparent" />
              <span className="absolute bottom-1/2 left-0 h-px w-full origin-left -rotate-[14deg] bg-[#ff6a1a]/60" />
            </span>
            <span className="flex flex-col gap-[3px]">
              <span className="h-1 w-full rounded-full bg-white/[0.09]" />
              <span className="h-1 w-3/4 rounded-full bg-white/[0.09]" />
            </span>
          </>
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[0.8125rem] font-medium text-white/85">{label}</span>
        <span className="mt-0.5 block text-[0.75rem] text-white/40">
          The screen, in full, with the reasoning behind it.
        </span>
      </span>
      <ArrowUpRight className="h-4 w-4 shrink-0 text-white/25 transition-colors group-hover:text-[#ff6a1a]" />
    </Link>
  )
}
